import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DownloadListService } from '../service/download-list.service';
import * as DownloadListActions from './download-list.actions';
import { catchError, map, switchMap, of, withLatestFrom, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { PageData, selectCurrentPages } from '@frontend/data-access-page';
import { DownloadListItem } from './download-list.models';
import { TranslateService } from '@ngx-translate/core';
import { selectDownloadList } from './download-list.selectors';
import { selectDocumentDetails } from '@frontend/data-access-document-details';

type DownloadListAction =
  | ReturnType<typeof DownloadListActions.downloadListSuccess>
  | ReturnType<typeof DownloadListActions.downloadListFailure>;

@Injectable()
export class DownloadListEffects {
  private actions$ = inject(Actions);
  private downloadListService = inject(DownloadListService);
  private store = inject(Store);
  private translate = inject(TranslateService);

  downloadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadListActions.downloadList),
      switchMap((action) => {
        const fileName =
          action.fileName && action.fileName.trim().length > 0 ? action.fileName : generateTimestampName();

        return this.downloadListService.downloadAllDocuments(action.documents, fileName, action.downloadMode).pipe(
          takeUntil(this.actions$.pipe(ofType(DownloadListActions.cancelDownload))),
          map((data) => {
            const isMergedPdf = action.downloadMode === 'merged_pdf';
            const blob = new Blob([data], { type: isMergedPdf ? 'application/pdf' : 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName + (isMergedPdf ? '.pdf' : '.zip');
            link.click();
            window.URL.revokeObjectURL(url);
            return DownloadListActions.downloadListSuccess({
              message: isMergedPdf
                ? 'PDF-Datei erfolgreich heruntergeladen.'
                : 'ZIP-Datei erfolgreich heruntergeladen.',
            });
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.error instanceof Blob && error.error.type === 'application/json') {
              return new Observable<DownloadListAction>((observer) => {
                const reader = new FileReader();
                reader.onload = () => {
                  try {
                    const errorJson = JSON.parse(reader.result as string);
                    observer.next(
                      DownloadListActions.downloadListFailure({
                        error: {
                          status: error.status,
                          message: errorJson.message || 'Ein unbekannter Fehler ist aufgetreten.',
                        },
                      })
                    );
                    observer.complete();
                  } catch (e) {
                    observer.next(
                      DownloadListActions.downloadListFailure({
                        error: {
                          status: error.status,
                          message: 'Fehlerdaten konnten nicht verarbeitet werden.',
                        },
                      })
                    );
                    observer.complete();
                  }
                };
                reader.onerror = () => {
                  observer.next(
                    DownloadListActions.downloadListFailure({
                      error: {
                        status: error.status,
                        message: 'Fehlerdaten konnten nicht gelesen werden.',
                      },
                    })
                  );
                  observer.complete();
                };
                reader.readAsText(error.error);
              });
            }

            return of(
              DownloadListActions.downloadListFailure({
                error: {
                  status: error.status,
                  message: 'Unbekannter Fehler beim Herunterladen.',
                },
              })
            );
          })
        );
      })
    )
  );

  addPageToDownloadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadListActions.addPageToDownloadListClicked),
      withLatestFrom(
        this.store.select(selectDownloadList),
        this.store.select(selectCurrentPages),
        this.store.select(selectDocumentDetails)
      ),
      switchMap(([action, downloadList, pages, doc]) => {
        const buildDownloadListItem = (page: PageData): DownloadListItem => ({
          documentId: action.documentId,
          categoryId: action.categoryId,
          title: `${doc?.title ?? ''}`,
          language: action.docuLanguage,
          extension: 'pdf',
          coverUrl: '', // fallback
          documentUrl: '', // fallback
          size: 0.3, // MB fallback
          start_page: page.index + 1,
          end_page: page.index + 1,
          start_page_nr: page.name,
        });

        const resultActions = [];

        const pageLeft = pages.find((p) => p.type === 'left');
        const pageRight = pages.find((p) => p.type === 'right');
        const currentPage = pages.find((p) => p.type === 'current');

        if (currentPage) {
          const item = buildDownloadListItem(currentPage);

          // Check: is currentPage included in an existing doubleItem
          // IMPORTANT: Do not remove chapters when adding individual pages
          const doubleItem = downloadList.find(
            (doc) =>
              doc.documentId === item.documentId &&
              doc.start_page !== doc.end_page &&
              (doc.start_page ?? 0) <= (item.start_page ?? 0) &&
              (doc.end_page ?? 0) >= (item.end_page ?? 0) &&
              doc.type !== 'chapter' // Skip chapters
          );

          if (doubleItem) {
            resultActions.push(
              DownloadListActions.removeFromDownloadList({
                documentId: doubleItem.documentId,
                start_page: doubleItem.start_page,
                end_page: doubleItem.end_page,
              })
            );

            // Add the remaining single page
            let otherPage, otherPageName;
            if (doubleItem.start_page === item.start_page) {
              otherPage = doubleItem.end_page;
              otherPageName = doubleItem.end_page_nr || '';
            } else {
              otherPage = doubleItem.start_page;
              otherPageName = doubleItem.start_page_nr || '';
            }

            resultActions.push(
              DownloadListActions.addToDownloadList({
                document: {
                  documentId: doubleItem.documentId,
                  categoryId: action.categoryId,
                  title: `${doc?.title ?? ''}`,
                  language: action.docuLanguage,
                  extension: 'pdf',
                  coverUrl: '',
                  documentUrl: '',
                  size: 0.3, // MB fallback
                  start_page: otherPage,
                  end_page: otherPage,
                  start_page_nr: `${otherPageName}`,
                },
              })
            );

            return of(...resultActions);
          }

          // Check: does a single currentPage already exist
          const exists = downloadList.some(
            (doc) =>
              doc.documentId === item.documentId && doc.start_page === item.start_page && doc.end_page === item.end_page
          );

          if (exists) {
            resultActions.push(
              DownloadListActions.removeFromDownloadList({
                documentId: item.documentId,
                start_page: item.start_page,
                end_page: item.end_page,
              })
            );
          } else {
            resultActions.push(DownloadListActions.addToDownloadList({ document: item }));
          }

          return of(...resultActions);
        }

        if (action.pageType === 'both' && pageLeft && pageRight) {
          const start = Math.min(pageLeft.index, pageRight.index) + 1;
          const end = Math.max(pageLeft.index, pageRight.index) + 1;
          const item: DownloadListItem = {
            documentId: action.documentId,
            categoryId: action.categoryId,
            title: `${doc?.title ?? ''}`,
            language: action.docuLanguage,
            extension: 'pdf',
            coverUrl: '',
            documentUrl: '',
            size: 0.6, // MB fallback
            start_page: start,
            end_page: end,
            start_page_nr: `${pageLeft.name}`,
            end_page_nr: `${pageRight.name}`,
          };

          const doubleExists = downloadList.some(
            (doc) =>
              doc.documentId === item.documentId && doc.start_page === item.start_page && doc.end_page === item.end_page
          );

          if (doubleExists) {
            resultActions.push(
              DownloadListActions.removeFromDownloadList({
                documentId: item.documentId,
                start_page: item.start_page,
                end_page: item.end_page,
              })
            );
          } else {
            // delete any single page if exists
            resultActions.push(
              ...downloadList
                .filter(
                  (doc) =>
                    doc.documentId === item.documentId &&
                    ((doc.start_page === start && doc.end_page === start) ||
                      (doc.start_page === end && doc.end_page === end))
                )
                .map((doc) =>
                  DownloadListActions.removeFromDownloadList({
                    documentId: doc.documentId,
                    start_page: doc.start_page,
                    end_page: doc.end_page,
                  })
                )
            );

            resultActions.push(DownloadListActions.addToDownloadList({ document: item }));
          }

          return of(...resultActions);
        }

        if ((action.pageType === 'left' || action.pageType === 'right') && (pageLeft || pageRight)) {
          const page = action.pageType === 'left' ? pageLeft : pageRight;
          if (!page) return of();

          const item = buildDownloadListItem(page);
          const resultActions = [];

          const exists = downloadList.some(
            (doc) =>
              doc.documentId === item.documentId && doc.start_page === item.start_page && doc.end_page === item.end_page
          );

          if (exists) {
            resultActions.push(
              DownloadListActions.removeFromDownloadList({
                documentId: item.documentId,
                start_page: item.start_page,
                end_page: item.end_page,
              })
            );
            return of(...resultActions);
          }

          // check if it's part of a double item
          // IMPORTANT: Do not remove chapters when adding individual pages
          const doubleItem = downloadList.find(
            (doc) =>
              doc.documentId === item.documentId &&
              doc.start_page !== doc.end_page &&
              (doc.start_page ?? 0) <= (item.start_page ?? 0) &&
              (doc.end_page ?? 0) >= (item.end_page ?? 0) &&
              doc.type !== 'chapter' // Skip chapters
          );

          if (doubleItem) {
            resultActions.push(
              DownloadListActions.removeFromDownloadList({
                documentId: doubleItem.documentId,
                start_page: doubleItem.start_page,
                end_page: doubleItem.end_page,
              })
            );

            const otherPage = doubleItem.start_page === item.start_page ? doubleItem.end_page : doubleItem.start_page;
            const otherPageName = doubleItem.start_page === item.start_page ? pageRight?.name : pageLeft?.name;

            resultActions.push(
              DownloadListActions.addToDownloadList({
                document: {
                  documentId: doubleItem.documentId,
                  categoryId: action.categoryId,
                  title: `${doc?.title ?? ''}`,
                  language: action.docuLanguage,
                  extension: 'pdf',
                  coverUrl: '',
                  documentUrl: '',
                  size: 0.3, // MB fallback
                  start_page: otherPage,
                  end_page: otherPage,
                  start_page_nr: `${otherPageName}`,
                },
              })
            );
          } else {
            resultActions.push(DownloadListActions.addToDownloadList({ document: item }));
          }

          return of(...resultActions);
        }
        return of();
      })
    )
  );
}

// Helper function for generating a timestamp-based name
function generateTimestampName(): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join('-');
}
